import type { TeamID, OrgID, DivisionID, MaxPrepID, PublicTeamID, EventID } from '@common/ids';
import { toTeamID, toMaxPrepID, toPublicTeamID } from '@common/ids';
import { PaidAccessLevel, toPaidAccessLevel } from '@common/paidAccessLevel';
import type { SeasonAndYear } from '@common/season';
import { Season, toSeason } from '@common/season';
import {
    InvalidSport,
    Sport,
    ValidSport,
    toSport,
    sportHasStatsEnabled,
    sportIsBaseballOrSoftball,
    sportIsFootballOrFlagfootball,
    sportIsValid,
    SPORTS_WITH_DELETE_CONFIRMATION,
    sportHasDeleteConfirmation,
} from '@common/sport';
import type { AgeGroup, AgeSchoolOptionType, PublicTeam } from '@common/team';
import { AgeDiscrete, AgeRange, AgeSchoolLevel, AgeSchoolOptions, NGB } from '@common/team';
import { isLiteralType, toLiteralType } from '@util/types';
import type * as TeamMember from '../teammember/types';

export type { TeamID, MaxPrepID, PublicTeamID, PublicTeam };
export type { AgeGroup, AgeSchoolOptionType };
export { AgeDiscrete, AgeRange, AgeSchoolLevel, AgeSchoolOptions, NGB };
export { toTeamID, toMaxPrepID, toPublicTeamID };
export { ValidSport, InvalidSport, Sport, toSport };
export {
    sportHasStatsEnabled,
    sportIsBaseballOrSoftball,
    sportIsFootballOrFlagfootball,
    sportIsValid,
    SPORTS_WITH_DELETE_CONFIRMATION,
    sportHasDeleteConfirmation,
};
export type { SeasonAndYear };
export { Season, toSeason };
export { PaidAccessLevel, toPaidAccessLevel };

export const NAMESPACE = 'team';

export const GoalSports: Sport[] = [Sport.LACROSSE, Sport.SOCCER, Sport.FIELD_HOCKEY, Sport.HOCKEY, Sport.WATER_POLO];
export const SportsWithPlayingPeriods: Sport[] = [Sport.BASKETBALL, Sport.FOOTBALL, Sport.RUGBY, ...GoalSports];

export enum CompetitionLevel {
    RECREATIONAL = 'recreational',
    CLUB_TRAVEL = 'club_travel',
    SCHOOL = 'school',
}

export enum TeamType {
    ADMIN = 'admin',
    OPPONENT = 'opponent',
}

export enum Role {
    STAFF = 'staff',
}

export interface Team {
    id: TeamID;
    name: string;
    sport: Sport;
    city?: string | null;
    state?: string | null;
    country?: string | null;
    ageGroup: AgeGroup;
    seasonName: Season;
    seasonYear: number;
    ngb?: string;
    competitionLevel: CompetitionLevel;
    teamType: TeamType;
    inningsPerGame?: number;
    settings?: TeamSettings;
    paidAccessLevel?: PaidAccessLevel;
    shortFielderType?: string;
    seasonRecord: string;
    avatar_url: string;
    publicID?: PublicTeamID;
    maxPrepsSchoolID?: string;
    /** The divisions the team has joined, or has been invited to, requested, etc. See `TeamDivision.status`. */
    divisions?: TeamDivision[];
}

export const teamDivisionStatuses = ['active', 'invited', 'joinRequest', 'blocked', 'rejected', 'deleted'] as const;
export type TeamDivisionStatus = (typeof teamDivisionStatuses)[number];
export const isTeamDivisionStatus = (str: string): str is TeamDivisionStatus =>
    isLiteralType(str, teamDivisionStatuses);

export interface TeamDivision {
    id: DivisionID;
    status: TeamDivisionStatus;
}

export interface TeamSettings {
    maxpreps?: MaxPrepsSettings;
}

interface MaxPrepsSettings {
    school_id: string;
    boxscore_sync: boolean;
}

export const isAgeGroup13U = (ageGroup: AgeGroup | string): boolean => {
    return (
        ageGroup === AgeDiscrete.EIGHT_AND_UNDER ||
        ageGroup === AgeDiscrete.NINE_AND_UNDER ||
        ageGroup === AgeDiscrete.TEN_AND_UNDER ||
        ageGroup === AgeDiscrete.ELEVEN_AND_UNDER ||
        ageGroup === AgeDiscrete.TWELVE_AND_UNDER ||
        ageGroup === AgeDiscrete.THIRTEEN_AND_UNDER ||
        ageGroup === AgeRange.UNDER_THIRTEEN ||
        ageGroup === AgeSchoolLevel.MIDDLE_TWELVE_AND_UNDER ||
        ageGroup === AgeSchoolLevel.ELEMENTARY
    );
};

const organizationAssocations = ['admin', 'scorekeeper'] as const;
export type OrganizationAssociation = (typeof organizationAssocations)[number];
export const toOrganizationAssocation = (str: string) =>
    toLiteralType(str, 'OrganizationAssociation', organizationAssocations);

type BaseListTeamOrOrg = {
    name: string;
    sport: Sport;
    seasonName: Season;
    seasonYear: number;
    displaySeason: string;
    avatarUrl?: string;
    maxPrepsSchoolID?: string;
};

export type ListTeam = BaseListTeamOrOrg & {
    id: TeamID;
    teamGroups: TeamMember.TeamGroup[];
    ageGroup: string;
    playerCount: number;
    seasonRecord?: string;
    publicID?: PublicTeamID;
    paidAccessLevel?: PaidAccessLevel;
};

export type ListOrg = BaseListTeamOrOrg & {
    id: OrgID;
    publicId?: string;
    association: OrganizationAssociation;
    playerCount: 0;
    type: string;
};

export type ListTeamOrOrg = ListTeam | ListOrg;

export const isListTeam = (x: ListTeamOrOrg): x is ListTeam => 'teamGroups' in x;
export const isListOrg = (x: ListTeamOrOrg): x is ListOrg => 'associations' in x;
export const isAdminOfListItem = (item: ListTeamOrOrg) => {
    if (isListTeam(item)) return item.teamGroups?.includes('manager');
    else return item.association === 'admin';
};

export interface TeamOrOrgListGroupedBySeason {
    [season: string]: ListTeamOrOrg[];
}
export interface TeamListGroupedBySeason<T extends ListTeam = ListTeam> {
    [season: string]: T[];
}

export interface MaxPrepSchool {
    id: MaxPrepID;
    name: string;
    city: string;
    state: string;
}

export type OrgLandingPageTeam = ListTeam & { hasJoinedOrg: boolean };

export interface TeamOpponent {
    id: TeamID;
    progenitorTeamID?: TeamID;
    name: string;
    avatarUrl?: string;
    playerCount: number;
    sport?: Sport;
    competitionLevel?: CompetitionLevel;
    ageGroup?: AgeGroup;
    isHidden: boolean;
}

export enum GameClockState {
    RUNNING = 'running',
    PAUSED = 'paused',
}

export interface PublicTeamLiveGame {
    gameId?: EventID;
    homeOrAway?: 'home' | 'away';
    isFullDay: boolean;
    start: string;
    end: string;
    lastScoringUpdate?: string;
    opponentTeam: {
        name: string;
        avatarUrl?: string;
    };
    score?: {
        opponentTeam: number;
        team: number;
    };
    videoStream?: {
        thumbnailUrl?: string;
        url?: string;
        streamedBy?: string;
        paidAccess?: boolean;
        audienceAccess?: boolean;
    };
    clock?: {
        state: GameClockState;
        endTime: string;
        displayTime: string;
        displayPeriod?: string;
    };
}
